import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import type {
  LocationFlags,
  LocationFormData,
  LocationVariant,
} from '#/features/order-create'
import {
  useCreateCustomerApiV1CustomersPost,
  useCreateCustomerContactApiV1CustomersCustomerIdContactsPost,
} from '#/shared/api/generated/shipment/theCargoShipment'
import type {
  CustomerContactCreate,
  CustomerCreate,
} from '#/shared/api/generated/shipment/models'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'
import { usPhoneToE164 } from '#/shared/lib/format'
import { toast } from '#/shared/ui/sonner'

const LOCATION_VARIANTS: readonly LocationVariant[] = ['origin', 'destination']

function splitContact(contact: string): { first: string; last: string | null } {
  const parts = contact.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return { first: '', last: null }
  const [first, ...rest] = parts
  return { first, last: rest.length > 0 ? rest.join(' ') : null }
}

function buildTerminalCustomer(
  data: LocationFormData['origin'],
): CustomerCreate {
  const { first, last } = splitContact(data.contact)
  const fallbackName = data.businessName.trim() || 'Terminal'
  return {
    first_name: first || fallbackName,
    last_name: last ?? undefined,
    company: data.businessName || undefined,
    phone: usPhoneToE164(data.phone) ?? undefined,
    secondary_phone: usPhoneToE164(data.mobile) ?? undefined,
    company_phone: usPhoneToE164(data.phone) ?? undefined,
    note: data.additionalNote || undefined,
    is_terminal: true,
  }
}

function buildTerminalContact(
  data: LocationFormData['origin'],
): CustomerContactCreate {
  const name = data.contact.trim()
  return {
    name: name || undefined,
    phone: usPhoneToE164(data.phone) ?? undefined,
    mobile: usPhoneToE164(data.mobile) ?? undefined,
    is_primary: true,
  }
}

export function useSaveNewTerminals() {
  const { t } = useTranslation()
  const createCustomer = useCreateCustomerApiV1CustomersPost()
  const createContact =
    useCreateCustomerContactApiV1CustomersCustomerIdContactsPost()

  const saveOne = async (data: LocationFormData['origin']) => {
    const created = await createCustomer.mutateAsync({
      data: buildTerminalCustomer(data),
    })
    await createContact.mutateAsync({
      customerId: created.data.id,
      data: buildTerminalContact(data),
    })
  }

  const save = useCallback(
    async (location: LocationFormData, terminalSaved: LocationFlags) => {
      for (const variant of LOCATION_VARIANTS) {
        if (!terminalSaved[variant]) continue
        try {
          await saveOne(location[variant])
        } catch (error) {
          toast.error(
            getApiErrorDetail(error) ?? t('orderCreate.terminal.saveError'),
          )
        }
      }
    },
    [createCustomer, createContact, t],
  )

  return {
    save,
    isSaving: createCustomer.isPending || createContact.isPending,
  }
}
